import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";
import { useDash } from "@/pages/dashboard/ctx";
import { SelfManagedSwitch, selfManagedLabel } from "@/components/SelfManagedSwitch";
import { SetByRedline } from "@/components/SettingsSection";

// Portfolio → Settings (Redline only): the firm-wide Self-managed default. Every community without its
// own override — including ones created later — inherits it; overrides are listed so nothing is hidden.
export function CompanySelfManagedCard({ companyId }) {
  const { enterCommunity } = useDash();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/companies/${companyId}/self-managed`).then((r) => setD(r.data)).catch(() => {}), [companyId]);
  useEffect(() => { load(); }, [load]);
  const set = async (v) => {
    setBusy(true);
    try {
      const r = await api.put(`/companies/${companyId}/self-managed`, { level: v });
      setD(r.data);
      toast.success(v === "off" ? "Portfolio back to Redline-managed" : `Portfolio self-managed by ${selfManagedLabel(v).toLowerCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };
  if (!d) return null;
  const level = d.self_managed_default || "off";
  return (
    <div data-testid="company-self-managed-card" className="rounded-2xl border border-sand-200 bg-white p-5 md:px-6 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[15px] flex items-center gap-2 flex-wrap">Self-managed {V.lcs("org")}
            <span data-testid="company-self-managed-summary" className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${level === "off" ? "bg-sand-100 text-ink/60" : "bg-emerald-100 text-emerald-700"}`}>
              {level === "off" ? "Redline-managed" : selfManagedLabel(level)}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">The default for all {d.communities} {V.lcs("org")} in this portfolio, and any added later: who may change a {V.lc("org")}'s concierge, channels and modules without asking Redline. Advanced dials, billing and add-ons always stay with Redline.</div>
        </div>
        <SetByRedline testid="company-self-managed-set-by" className="shrink-0" />
      </div>
      <SelfManagedSwitch value={level} onChange={set} busy={busy} testid="company-self-managed" />
      {d.overrides?.length > 0 && (
        <div className="text-xs text-ink/70" data-testid="company-self-managed-overrides">
          <span className="font-semibold text-ink">{d.overrides.length} {d.overrides.length === 1 ? V.lc("org") : V.lcs("org")} set individually</span> (unaffected by this default):{" "}
          {d.overrides.map((o, i) => (
            <span key={o.id}>{i > 0 && ", "}<button type="button" onClick={() => enterCommunity(o.id, "/dashboard/settings?tab=access")} className="text-redline-600 hover:underline" data-testid={`company-self-managed-override-${o.id}`}>{o.name}</button> · {selfManagedLabel(o.self_managed)}</span>
          ))}
        </div>
      )}
    </div>
  );
}
